import { LevelType, SectionType, SpaceType } from 'data/types';

export type SectionToSaveType = SectionType & {
  levelUniqueId: string;
  floorNum: number;
};

export type StructureItemIds = {
  levels?: string[];
  sections?: string[];
  spaces?: string[];
};

/** Transient 5xx/network errors retry this many times, then the item is quarantined. */
export const STRUCTURE_SAVE_FAILURE_LIMIT = 3;

function toIdSet(
  ids: ReadonlySet<string> | string[] | undefined
): ReadonlySet<string> {
  if (!ids) return new Set();
  return ids instanceof Set ? ids : new Set(ids);
}

function sectionNeedsBaselineShell(
  section: SectionType,
  sectionIds: ReadonlySet<string>,
  spaceIds: ReadonlySet<string>
): boolean {
  if (sectionIds.has(section.uniqueId)) return true;
  return section.spaces.some((space) => spaceIds.has(space.uniqueId));
}

function levelNeedsBaselineShell(
  level: LevelType,
  levelIds: ReadonlySet<string>,
  sectionIds: ReadonlySet<string>,
  spaceIds: ReadonlySet<string>
): boolean {
  if (levelIds.has(level.uniqueId)) return true;
  return level.sections.some(
    (section) =>
      sectionIds.has(section.uniqueId) ||
      section.spaces.some((space) => spaceIds.has(space.uniqueId))
  );
}

function mergeSectionSpaces(
  section: SectionType,
  liveSection: SectionType | undefined,
  spaceIds: ReadonlySet<string>
): SectionType {
  const liveSpaces = liveSection?.spaces ?? [];
  const liveSpaceById = new Map(
    liveSpaces.map((space) => [space.uniqueId, space])
  );
  const baselineSpaceIds = new Set(
    section.spaces.map((space) => space.uniqueId)
  );

  const mergedSpaces = section.spaces.map((baseSpace) =>
    spaceIds.has(baseSpace.uniqueId)
      ? liveSpaceById.get(baseSpace.uniqueId) ?? baseSpace
      : baseSpace
  );
  const addedSpaces = liveSpaces.filter(
    (space) =>
      spaceIds.has(space.uniqueId) && !baselineSpaceIds.has(space.uniqueId)
  );

  return {
    ...section,
    spaces: [...mergedSpaces, ...addedSpaces]
  };
}

function mergeLevelChildren(
  level: LevelType,
  liveLevel: LevelType | undefined,
  sectionIds: ReadonlySet<string>,
  spaceIds: ReadonlySet<string>
): LevelType {
  const liveSections = liveLevel?.sections ?? [];
  const liveSectionById = new Map(
    liveSections.map((section) => [section.uniqueId, section])
  );
  const baselineSectionIds = new Set(
    level.sections.map((section) => section.uniqueId)
  );

  const mergedSections = level.sections.map((baseSection) => {
    const liveSection = liveSectionById.get(baseSection.uniqueId);
    const sectionMeta =
      sectionIds.has(baseSection.uniqueId) && liveSection
        ? { ...liveSection, spaces: baseSection.spaces }
        : baseSection;
    return mergeSectionSpaces(sectionMeta, liveSection, spaceIds);
  });

  const addedSections = liveSections
    .filter(
      (section) =>
        !baselineSectionIds.has(section.uniqueId) &&
        sectionNeedsBaselineShell(section, sectionIds, spaceIds)
    )
    .map((section) =>
      mergeSectionSpaces({ ...section, spaces: [] }, section, spaceIds)
    );

  return {
    ...level,
    sections: [...mergedSections, ...addedSections]
  };
}

export function mergeSpacesIntoStructureBaseline(
  baseline: LevelType[],
  live: LevelType[],
  affectedSpaceUniqueIds: string[]
): LevelType[] {
  if (affectedSpaceUniqueIds.length === 0) return baseline;

  const affectedIds = new Set(affectedSpaceUniqueIds);
  const liveSpacesById = new Map<string, SpaceType>();
  live.forEach((level) => {
    level.sections.forEach((section) => {
      section.spaces.forEach((space) => {
        if (affectedIds.has(space.uniqueId)) {
          liveSpacesById.set(space.uniqueId, space);
        }
      });
    });
  });

  if (liveSpacesById.size === 0) return baseline;

  return baseline.map((level) => ({
    ...level,
    sections: level.sections.map((section) => ({
      ...section,
      spaces: section.spaces.map(
        (space) => liveSpacesById.get(space.uniqueId) ?? space
      )
    }))
  }));
}

export function collectUnpersistedParentSections(
  structure: LevelType[],
  parentSectionUniqueIds: string[],
  options: {
    persistedSectionIds: ReadonlySet<string>;
    abandonedSectionIds?: ReadonlySet<string>;
    alreadySavingSectionIds?: ReadonlySet<string> | string[];
  }
): SectionToSaveType[] {
  const parentIds = new Set(parentSectionUniqueIds);
  const persistedIds = options.persistedSectionIds;
  const abandonedIds = options.abandonedSectionIds ?? new Set<string>();
  const alreadySaving = toIdSet(options.alreadySavingSectionIds);
  const sectionsToEnsure: SectionToSaveType[] = [];

  structure.forEach((level, levelIndex) => {
    level.sections.forEach((section) => {
      if (
        parentIds.has(section.uniqueId) &&
        !persistedIds.has(section.uniqueId) &&
        !abandonedIds.has(section.uniqueId) &&
        !alreadySaving.has(section.uniqueId)
      ) {
        sectionsToEnsure.push({
          ...section,
          levelUniqueId: level.uniqueId,
          floorNum: levelIndex
        });
      }
    });
  });

  return sectionsToEnsure;
}

export function isExplicitSaveRejection(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  if (!('status' in error)) return false;
  const { status } = error;
  return typeof status === 'number' && status >= 400 && status < 500;
}

export function recordStructureSaveFailures(
  failureCounts: Map<string, number>,
  uniqueIds: string[]
): string[] {
  const quarantined: string[] = [];
  uniqueIds.forEach((id) => {
    const next = (failureCounts.get(id) ?? 0) + 1;
    if (next >= STRUCTURE_SAVE_FAILURE_LIMIT) {
      failureCounts.delete(id);
      quarantined.push(id);
    } else {
      failureCounts.set(id, next);
    }
  });
  return quarantined;
}

export function quarantineFailedSaveIds(
  failureCounts: Map<string, number>,
  uniqueIds: string[],
  error: unknown
): string[] {
  if (isExplicitSaveRejection(error)) {
    uniqueIds.forEach((id) => failureCounts.delete(id));
    return [...uniqueIds];
  }
  return recordStructureSaveFailures(failureCounts, uniqueIds);
}

export function clearStructureSaveFailures(
  failureCounts: Map<string, number>,
  uniqueIds: string[]
): void {
  uniqueIds.forEach((id) => failureCounts.delete(id));
}

export function classifyStructureItemIds(
  structure: LevelType[],
  uniqueIds: string[]
): Required<StructureItemIds> {
  const idSet = new Set(uniqueIds);
  const levels: string[] = [];
  const sections: string[] = [];
  const spaces: string[] = [];

  structure.forEach((level) => {
    if (idSet.has(level.uniqueId)) {
      levels.push(level.uniqueId);
    }
    level.sections.forEach((section) => {
      if (idSet.has(section.uniqueId)) {
        sections.push(section.uniqueId);
      }
      section.spaces.forEach((space) => {
        if (idSet.has(space.uniqueId)) {
          spaces.push(space.uniqueId);
        }
      });
    });
  });

  return { levels, sections, spaces };
}

export function spaceUniqueIdsWithAbandonedParents(
  structure: LevelType[],
  abandonedSectionIds: ReadonlySet<string>
): string[] {
  if (abandonedSectionIds.size === 0) return [];
  const spaceIds: string[] = [];
  structure.forEach((level) => {
    level.sections.forEach((section) => {
      if (!abandonedSectionIds.has(section.uniqueId)) return;
      section.spaces.forEach((space) => spaceIds.push(space.uniqueId));
    });
  });
  return spaceIds;
}

export function omitItemsFromStructureBaseline(
  baseline: LevelType[],
  ids: StructureItemIds
): LevelType[] {
  const levelIds = new Set(ids.levels ?? []);
  const sectionIds = new Set(ids.sections ?? []);
  const spaceIds = new Set(ids.spaces ?? []);
  if (levelIds.size === 0 && sectionIds.size === 0 && spaceIds.size === 0) {
    return baseline;
  }

  return baseline
    .filter((level) => !levelIds.has(level.uniqueId))
    .map((level) => ({
      ...level,
      sections: level.sections
        .filter((section) => !sectionIds.has(section.uniqueId))
        .map((section) => ({
          ...section,
          spaces: section.spaces.filter(
            (space) => !spaceIds.has(space.uniqueId)
          )
        }))
    }));
}

/**
 * Copy the given live items into the autosave baseline so they stop
 * recomputing as changed. Children are not adopted unless their ids are
 * listed — a failed level save must not hide unsaved sections/spaces.
 */
export function mergeItemsIntoStructureBaseline(
  baseline: LevelType[],
  live: LevelType[],
  ids: StructureItemIds
): LevelType[] {
  const levelIds = new Set(ids.levels ?? []);
  const sectionIds = new Set(ids.sections ?? []);
  const spaceIds = new Set(ids.spaces ?? []);
  if (levelIds.size === 0 && sectionIds.size === 0 && spaceIds.size === 0) {
    return baseline;
  }

  const liveById = new Map(live.map((level) => [level.uniqueId, level]));
  const baselineLevelIds = new Set(baseline.map((level) => level.uniqueId));

  const mergedBaseline = baseline.map((baseLevel) => {
    const liveLevel = liveById.get(baseLevel.uniqueId);
    const levelMeta =
      levelIds.has(baseLevel.uniqueId) && liveLevel
        ? { ...liveLevel, sections: baseLevel.sections }
        : baseLevel;

    return mergeLevelChildren(levelMeta, liveLevel, sectionIds, spaceIds);
  });

  const newLevels = live
    .filter(
      (level) =>
        !baselineLevelIds.has(level.uniqueId) &&
        levelNeedsBaselineShell(level, levelIds, sectionIds, spaceIds)
    )
    .map((level) =>
      mergeLevelChildren(
        {
          ...level,
          sections: []
        },
        level,
        sectionIds,
        spaceIds
      )
    );

  return [...mergedBaseline, ...newLevels];
}
